import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  ArrowLeft, Plus, Edit2, Trash2, ChevronDown, ChevronUp, 
  Filter, Search, Globe, Package, Car, HelpCircle, Eye, EyeOff
} from 'lucide-react';
import { faqService } from '../services/faqService';
import { assetService } from '../services/assetService';
import { curationService } from '../services/curationService';

const FAQPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [faqs, setFaqs] = useState([]);
  const [filteredFAQs, setFilteredFAQs] = useState([]);
  const [assets, setAssets] = useState([]);
  const [packages, setPackages] = useState([]);
  const [error, setError] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'how-it-works',
    asset_id: '',
    package_id: '',
    order_index: 0,
    is_active: true
  });

  const categories = [
    { value: 'how-does-it-work', label: 'How Does It Work FAQ', icon: Globe },
    { value: 'individual-assets', label: 'Individual Assets FAQ', icon: Car },
    { value: 'curated-assets', label: '360 Days Curation FAQ', icon: Package },
    { value: 'help-centre', label: 'Help Centre FAQ', icon: HelpCircle }
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    filterFAQs();
  }, [faqs, selectedCategory, searchTerm]);

  const fetchAllData = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch FAQs, assets, and packages
      const [faqsData, assetsData, packagesData] = await Promise.all([
        faqService.getAllFAQs(),
        assetService.getAllAssets().catch(() => []),
        curationService.getAllPackages().catch(() => [])
      ]);
      
      setFaqs(faqsData.sort((a, b) => (a.order_index || 0) - (b.order_index || 0)));
      setAssets(assetsData);
      setPackages(packagesData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filterFAQs = () => {
    let filtered = faqs;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(faq => faq.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(faq => 
        faq.question.toLowerCase().includes(search) ||
        faq.answer.toLowerCase().includes(search)
      );
    }

    setFilteredFAQs(filtered);
  };

  const handleOpenDialog = (faq = null) => {
    if (faq) {
      setEditingFAQ(faq);
      setFormData({
        question: faq.question || '',
        answer: faq.answer || '',
        category: faq.category || 'how-it-works',
        asset_id: faq.asset_id || '',
        package_id: faq.package_id || '',
        order_index: faq.order_index || 0,
        is_active: faq.is_active !== false
      });
    } else {
      setEditingFAQ(null);
      setFormData({
        question: '',
        answer: '',
        category: 'how-it-works',
        asset_id: '',
        package_id: '',
        order_index: faqs.length,
        is_active: true
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingFAQ(null);
    setFormData({ 
      question: '', 
      answer: '', 
      category: 'how-it-works', 
      asset_id: '',
      package_id: '',
      order_index: 0, 
      is_active: true 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const submitData = { ...formData };
      
      // Clear asset_id or package_id based on category
      if (submitData.category !== 'individual-assets') {
        submitData.asset_id = null;
      }
      if (submitData.category !== 'curated-assets') {
        submitData.package_id = null;
      }
      
      if (editingFAQ) {
        await faqService.updateFAQ(editingFAQ.id, submitData);
        toast.success('FAQ updated successfully!');
      } else {
        await faqService.createFAQ(submitData);
        toast.success('FAQ created successfully!');
      }
      await fetchAllData();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving FAQ:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to save FAQ';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (faq) => {
    if (!window.confirm(`Are you sure you want to delete the FAQ "${faq.question}"?`)) return;
    
    setError('');
    try {
      await faqService.deleteFAQ(faq.id);
      toast.success('FAQ deleted successfully!');
      await fetchAllData();
    } catch (err) {
      console.error('Error deleting FAQ:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to delete FAQ';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const toggleActive = async (faq) => {
    setError('');
    try {
      await faqService.updateFAQ(faq.id, { is_active: !faq.is_active });
      toast.success(`FAQ ${!faq.is_active ? 'activated' : 'deactivated'} successfully!`);
      await fetchAllData();
    } catch (err) {
      console.error('Error updating FAQ:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to update FAQ';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const getCategoryIcon = (category) => {
    const categoryData = categories.find(cat => cat.value === category);
    const Icon = categoryData?.icon || HelpCircle;
    return <Icon className="w-4 h-4" />;
  };

  const getCategoryLabel = (category) => {
    return categories.find(cat => cat.value === category)?.label || category;
  };

  const getAssetName = (assetId) => {
    const asset = assets.find(a => a.id === assetId);
    return asset ? asset.name : 'Unknown Asset';
  };

  const getPackageName = (packageId) => {
    const pkg = packages.find(p => p.id === packageId);
    return pkg ? pkg.name : 'Unknown Package';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notifications */}
      <Toaster />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2"
              >
                <ArrowLeft size={16} />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">FAQ Management</h1>
                <p className="text-sm text-gray-600 mt-1">Manage FAQs for different sections of the website</p>
              </div>
            </div>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-black hover:bg-gray-800 text-white flex items-center gap-2"
            >
              <Plus size={16} />
              Add FAQ
            </Button>
          </div>

          {/* Filters */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Category Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Filter by Category</Label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Search FAQs</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  type="text"
                  placeholder="Search questions or answers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {/* Category Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {categories.map(category => {
            const categoryFAQs = faqs.filter(faq => faq.category === category.value);
            const activeFAQs = categoryFAQs.filter(faq => faq.is_active);
            const Icon = category.icon;
            
            return (
              <Card key={category.value} className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setSelectedCategory(selectedCategory === category.value ? 'all' : category.value)}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{category.label}</p>
                      <p className="text-xs text-gray-600">{activeFAQs.length}/{categoryFAQs.length} active</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQs List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center justify-between">
              <span>
                {selectedCategory === 'all' ? 'All FAQs' : getCategoryLabel(selectedCategory)} 
                ({filteredFAQs.length})
              </span>
              {selectedCategory !== 'all' && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setSelectedCategory('all')}
                >
                  Clear Filter
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <p className="mt-4 text-gray-600">Loading FAQs...</p>
              </div>
            ) : filteredFAQs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {searchTerm || selectedCategory !== 'all' ? 
                  'No FAQs found matching your criteria.' : 
                  'No FAQs created yet. Click "Add FAQ" to create one.'
                }
              </div>
            ) : (
              <div className="space-y-3">
                {filteredFAQs.map((faq) => (
                  <div
                    key={faq.id}
                    className={`border rounded-lg ${faq.is_active ? 'border-gray-200 bg-white' : 'border-gray-300 bg-gray-50'}`}
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {getCategoryIcon(faq.category)}
                          <div>
                            <h3 className="font-medium text-gray-900">{faq.question}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                {getCategoryLabel(faq.category)}
                              </span>
                              {faq.asset_id && (
                                <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                                  Asset: {getAssetName(faq.asset_id)}
                                </span>
                              )}
                              {faq.package_id && (
                                <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                                  Package: {getPackageName(faq.package_id)}
                                </span>
                              )}
                              {!faq.is_active && (
                                <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded flex items-center gap-1">
                                  <EyeOff className="w-3 h-3" />
                                  Inactive
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleActive(faq)}
                            className="text-xs flex items-center gap-1"
                          >
                            {faq.is_active ? (
                              <><EyeOff className="w-3 h-3" />Hide</>
                            ) : (
                              <><Eye className="w-3 h-3" />Show</>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenDialog(faq)}
                          >
                            <Edit2 size={14} />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(faq)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="cursor-pointer" onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)}>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          {expandedFAQ === faq.id ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                          <span>{expandedFAQ === faq.id ? 'Hide' : 'Show'} answer</span>
                        </div>
                        {expandedFAQ === faq.id && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFAQ ? 'Edit FAQ' : 'Add New FAQ'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="question">Question *</Label>
              <Input
                id="question"
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                placeholder="Enter the question"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="answer">Answer *</Label>
              <textarea
                id="answer"
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                placeholder="Enter the answer"
                required
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value, asset_id: '', package_id: '' })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="order_index">Order</Label>
                <Input
                  id="order_index"
                  type="number"
                  value={formData.order_index}
                  onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
            </div>

            {/* Asset Selection for Individual Assets category */}
            {formData.category === 'individual-assets' && (
              <div className="space-y-2">
                <Label htmlFor="asset_id">Specific Asset (Optional)</Label>
                <select
                  id="asset_id"
                  value={formData.asset_id}
                  onChange={(e) => setFormData({ ...formData, asset_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900"
                >
                  <option value="">All Individual Assets</option>
                  {assets.map(asset => (
                    <option key={asset.id} value={asset.id}>{asset.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">
                  Leave empty to show this FAQ on all individual asset pages
                </p>
              </div>
            )}

            {/* Package Selection for Curated Assets category */}
            {formData.category === 'curated-assets' && (
              <div className="space-y-2">
                <Label htmlFor="package_id">Specific Package (Optional)</Label>
                <select
                  id="package_id"
                  value={formData.package_id}
                  onChange={(e) => setFormData({ ...formData, package_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900"
                >
                  <option value="">All Curated Packages</option>
                  {packages.map(pkg => (
                    <option key={pkg.id} value={pkg.id}>{pkg.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">
                  Leave empty to show this FAQ on all curated package pages
                </p>
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Active (visible to website users)</Label>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" className="bg-black hover:bg-gray-800 text-white">
                {editingFAQ ? 'Update FAQ' : 'Create FAQ'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FAQPage;
